import React from 'react';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import usePageMeta from '../../hooks/usePageMeta';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(12),
    marginBottom: theme.spacing(8),
    textAlign: 'left',
    maxWidth: 800,
  },
  section: {
    marginTop: theme.spacing(6),
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
  },
}));

export default function About() {
  const classes = useStyles();
  usePageMeta('About Us', 'Why The Warrior Tribe exists and how our Chrome extension helps people build lasting digital habits.');

  return (
    <Container className={classes.root}>
      <Typography variant="h3" gutterBottom>
        About The Warrior Tribe
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        The Warrior Tribe is a digital wellbeing platform built for people who have decided to take back control of their attention, their habits, and their lives. We believe that compulsive consumption of explicit online content is a serious and widely underacknowledged problem — and that the people affected by it deserve practical, effective tools to help them change.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We are not a moral crusade. We are not here to judge how anyone has lived. We are here because the evidence is clear: for millions of people, habitual explicit content consumption quietly drains their energy, undermines their relationships, compromises their professional performance, and disconnects them from the things that actually matter to them. And most of them have no idea why.
      </Typography>

      <div className={classes.section}>
        <Typography variant="h4" gutterBottom>
          Why We Built This
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Warrior Tribe started from a simple observation: willpower alone is not enough. The research on behavioral addiction is clear — the brain regions responsible for impulse control are precisely the ones most compromised by compulsive habits. Telling yourself to "just stop" is asking the weakest part of a compromised system to overrule the strongest part. It rarely works for long.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          What does work is structural support. Removing easy access. Creating friction between the impulse and the action. Building a visible record of progress that makes each clean day feel like an achievement worth protecting. That is the philosophy behind everything we have built.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Our Chrome extension automatically blocks adult websites the moment you install it — no configuration, no willpower required. The XP and streak system turns your recovery into something tangible and rewarding. Every day you stay clean, your avatar grows stronger. Every streak you build is a real record of real progress.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h4" gutterBottom>
          What We Stand For
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Honesty over comfort.</strong> We do not soften the reality of what compulsive explicit content use does to people. The science is available and it is compelling. We think people deserve to know the truth about what is happening in their brains and their lives, and to make informed decisions based on that truth.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Practical tools over good intentions.</strong> Good intentions without structural support produce relapse. We focus on building tools that work in the real moments when habits are hardest to resist — not just during moments of calm reflection.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Progress over perfection.</strong> Recovery is not linear. Slips happen. The Warrior Tribe is not designed to punish failure — it is designed to make progress visible and to make getting back on track feel accessible rather than shameful.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Privacy above all.</strong> We collect only what is strictly necessary to operate the service. We do not sell data. We do not share your information with advertisers. Your struggle and your progress are yours alone.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h4" gutterBottom>
          Who Uses The Warrior Tribe
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Our users include students who have noticed their focus disappearing, professionals who feel their ambition draining away, people in relationships who sense a growing distance they cannot explain, and individuals who have simply reached the point where they want their lives back. They come from every background, every age group, and every part of the world.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          What they share is the decision to do something about it — and the willingness to use a practical tool to back that decision up. That is what the Warrior Tribe is built for.
        </Typography>
      </div>

      <div className={classes.section}>
        <Typography variant="h4" gutterBottom>
          Get Started
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Installing the extension takes under two minutes. A 14-day free trial gives you full access to all features. If you want to understand more about the science and the experience of recovery before getting started, our <Link href="/blog" color="primary">blog</Link> covers the research in depth.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Questions? Reach us at <Link href="mailto:support@thewarriortribe.xyz" color="primary">support@thewarriortribe.xyz</Link>.
        </Typography>
      </div>
    </Container>
  );
}
